import React from "react";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLightbulb,
  faScroll,
  faMicrophone,
  faVideo,
  faUpload,
  faWandMagicSparkles,
  faCut,
  faEnvelope,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { PanelWembii } from "../PanelWembii";
import { WorkflowId } from "../../../cores/videoGen/types";
import cn from "classnames";

export const WORKFLOW_CONFIG: Record<
  WorkflowId,
  {
    icon: typeof faLightbulb;
    labelId: string;
    descriptionId: string;
    tag?: string;
    imageUrl?: string;
  }
> = {
  ideaToVideo: {
    icon: faLightbulb,
    labelId: "app.videoGen.workflow.ideaToVideo",
    descriptionId: "app.videoGen.workflow.ideaToVideoDesc",
    tag: "Popular",
    imageUrl:
      "https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg?auto=compress&cs=tinysrgb&w=800", // persona escribiendo ideas
  },
  scriptToVideo: {
    icon: faScroll,
    labelId: "app.videoGen.workflow.scriptToVideo",
    descriptionId: "app.videoGen.workflow.scriptToVideoDesc",
    imageUrl:
      "https://images.pexels.com/photos/2706379/pexels-photo-2706379.jpeg?auto=compress&cs=tinysrgb&w=800", // guion impreso y notas
  },
  voiceoverToVideo: {
    icon: faMicrophone,
    labelId: "app.videoGen.workflow.voiceoverToVideo",
    descriptionId: "app.videoGen.workflow.voiceoverToVideoDesc",
    tag: "Pro",
    imageUrl:
      "https://images.pexels.com/photos/6898859/pexels-photo-6898859.jpeg?auto=compress&cs=tinysrgb&w=800", // microfono en estudio
  },
  record: {
    icon: faVideo,
    labelId: "app.videoGen.workflow.record",
    descriptionId: "app.videoGen.workflow.recordDesc",
    tag: "Pro",
    imageUrl:
      "https://images.pexels.com/photos/6898852/pexels-photo-6898852.jpeg?auto=compress&cs=tinysrgb&w=800", // persona grabándose
  },
  uploadEdit: {
    icon: faUpload,
    labelId: "app.videoGen.workflow.uploadEdit",
    descriptionId: "app.videoGen.workflow.uploadEditDesc",
    tag: "Pro",
    imageUrl:
      "https://images.pexels.com/photos/6898850/pexels-photo-6898850.jpeg?auto=compress&cs=tinysrgb&w=800", // timeline de edición
  },
  aiClip: {
    icon: faWandMagicSparkles,
    labelId: "app.videoGen.workflow.aiClip",
    descriptionId: "app.videoGen.workflow.aiClipDesc",
    tag: "Negocios",
    imageUrl:
      "https://images.pexels.com/photos/8386434/pexels-photo-8386434.jpeg?auto=compress&cs=tinysrgb&w=800", // clip corto dinámico
  },
  editor: {
    icon: faCut,
    labelId: "app.videoGen.workflow.editor",
    descriptionId: "app.videoGen.workflow.editorDesc",
    imageUrl:
      "https://images.pexels.com/photos/6898851/pexels-photo-6898851.jpeg?auto=compress&cs=tinysrgb&w=800", // editor de video en pantalla
  },
  feedback: {
    icon: faEnvelope,
    labelId: "app.videoGen.workflow.feedback",
    descriptionId: "app.videoGen.workflow.feedbackDesc",
  },
};

interface WorkflowSelectorProps {
  onSelect: (workflow: WorkflowId) => void;
  aspectRatioLabel?: string;
  onClose?: () => void;
}

export function WorkflowSelector({
  onSelect,
  aspectRatioLabel,
  onClose,
}: WorkflowSelectorProps) {
  const intl = useIntl();

  const workflows: WorkflowId[] = [
    "ideaToVideo",
    "scriptToVideo",
    "voiceoverToVideo",
    "record",
    "uploadEdit",
    "aiClip",
    "editor",
    "feedback",
  ];

  return (
    <div className="video-gen-workflow">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          {intl.formatMessage({ id: "app.videoGen.chooseWorkflow" })}
        </h2>
        <div className="d-flex align-items-center gap-2">
          {aspectRatioLabel && (
            <span className="video-gen-aspect-badge">{aspectRatioLabel}</span>
          )}
          {onClose && (
            <button
              type="button"
              className="btn btn-link p-0 text-secondary"
              onClick={onClose}
              aria-label="Cerrar"
            >
              <FontAwesomeIcon icon={faXmark} size="lg" />
            </button>
          )}
        </div>
      </div>
      <div className="row g-3">
        {workflows.map((id) => {
          const config = WORKFLOW_CONFIG[id as keyof typeof WORKFLOW_CONFIG];
          if (!config) return null;
          const isFeedback = id === "feedback";
          return (
            <div key={id} className="col-12 col-md-6 col-lg-4 col-xl-3">
              <PanelWembii
                shadow
                className={cn(
                  "video-gen-workflow-card h-100 pointer border",
                  { "video-gen-workflow-card--feedback": isFeedback }
                )}
                onClick={() => !isFeedback && onSelect(id as WorkflowId)}
              >
                <div className="video-gen-workflow-card__image">
                  {config.imageUrl ? (
                    <img
                      src={config.imageUrl}
                      alt={intl.formatMessage({ id: config.labelId })}
                      className="video-gen-workflow-card__image-img"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={config.icon}
                      className="video-gen-workflow-card__icon"
                    />
                  )}
                </div>
                {config.tag && (
                  <span className="video-gen-workflow-card__tag">
                    {config.tag}
                  </span>
                )}
                <h6 className="mt-2 mb-1">{intl.formatMessage({ id: config.labelId })}</h6>
                <p className="small text-muted mb-0">
                  {intl.formatMessage({ id: config.descriptionId })}
                </p>
              </PanelWembii>
            </div>
          );
        })}
      </div>
    </div>
  );
}
